import { window } from "global";
import classNames from "classnames";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useUGGAssetsContext } from "@ugg/shared/components/UGGAssetsProvider";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { useLanguage } from "@ugg/shared/hooks/use-language-hooks";
import { useChampionProfileContext } from "@ugg/shared/components/Champions/ChampionProfileContext";
import { useAramMayhemPerChampionAugmentTierList, useAramMayhemAugmentManifest } from "@ugg/shared/hooks/aram-mayhem";

interface AramMayhemSEOProps {
  className?: string;
  patch: string;
  displayPatch: string;
}

// Grammar-aware blurb assembly — split out because the four count
// cases (0 / 1 / 2 / 3) each need distinct wording ("Augment" vs
// "Augments", "is" vs "are", "an S+ pick" vs "both S+ picks" vs
// "each are S+ picks") and inlining the ternaries would obscure
// what the actual sentence looks like at each length.
//
// The caller must slice `augmentNames` to at most 3 entries before
// invoking this — the 3-item branch destructures the first three
// and would produce a broken sentence with more than that.
function buildAugmentBlurb(championName: string, augmentNames: string[], displayPatch: string): string {
  const closer = `Follow this quick guide that ranks every LoL ${championName} Aram Augment to win more in ${displayPatch}.`;

  // 0 augments — the user-specified fallback for champions that
  // don't have S+ picks in the tier list (typically because the
  // per-champion sample size hasn't cleared MIN_SAMPLES yet).
  // Also the state during initial load before the augment fetches
  // resolve — better to render meaningful text on first paint
  // than to blank the whole SEO block waiting for augment data.
  if (augmentNames.length === 0) {
    return `The best ARAM Mayhem Augments for ${championName}. ${closer}`;
  }

  if (augmentNames.length === 1) {
    return `The best ARAM Mayhem Augment for ${championName} is ${augmentNames[0]}, an S+ pick when playing ARAM Mayhem. ${closer}`;
  }

  if (augmentNames.length === 2) {
    return `The best ARAM Mayhem Augments for ${championName} are ${augmentNames[0]} and ${augmentNames[1]}, both S+ picks when playing ARAM Mayhem. ${closer}`;
  }

  // 3 (or accidentally more — we destructure the first three and
  // ignore any extras; the caller should have sliced already).
  const [a, b, c] = augmentNames;
  return `The best ARAM Mayhem Augments for ${championName} are ${a}, ${b}, and ${c}, each are S+ picks when playing ARAM Mayhem. ${closer}`;
}

export default function AramMayhemSEO(props: AramMayhemSEOProps) {
  const { className, displayPatch, patch } = props;
  const { championId, page } = useChampionProfileContext();

  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [lang] = useLanguage();

  const { staging } = useUGGAssetsContext();
  const envDir = staging ? "staging" : "prod";
  const {
    data: buildChampionNames,
    loading: fetchingBuildChampionNames,
    error: errorBuildChampionNames,
  } = useJSONFetcher(`https://static.bigbrain.gg/assets/lol/riot_patch_update/${envDir}/seo-champion-names.json`, { ssr: true });

  // Per-champion augment tier list + augment manifest — same URLs
  // AramMayhemPerChampionAugmentTierListContainer hits on this page,
  // so useJSONFetcher's URL-keyed cache dedupes: zero additional
  // network requests attributable to this SEO block. `patch` and
  // `championId` come from the same sources the container uses
  // (route props + ChampionProfileContext), so the cache keys line
  // up byte-for-byte. `ssr: true` on both so the blurb renders with
  // real augment names in the initial HTML for crawlers rather than
  // hydrating in after mount.
  const { data: perChampionAugmentTierList } = useAramMayhemPerChampionAugmentTierList({
    championKey: championId,
    params: { patch },
    ssr: true,
  });
  const { data: augmentManifest } = useAramMayhemAugmentManifest({
    params: { patch },
    ssr: true,
  });

  if (!window) {
    if (location.pathname.includes("/tr_tr")) {
      i18n.changeLanguage("tr_TR");
    }
  }

  if (!buildChampionNames) {
    return null;
  }

  const champion = buildChampionNames[championId];
  if (!champion) {
    return null;
  }
  const { name } = champion;

  // Resolve up to the first three S+ augment IDs into display
  // names via the manifest. Defensive access all the way down —
  // any of these being missing (tier list still loading, S+
  // bucket empty, manifest entry absent for an id, nameTRA
  // empty) lands us at a shorter or empty augmentNames list,
  // which buildAugmentBlurb handles by falling through to the
  // matching count-branch (0 → no-augments fallback, 1 → single
  // form, 2 → paired form, 3 → full form).
  //
  // Manifest keys are stringified augment ids (`str(aug_id)` on
  // the Python side in build_augment_manifest), so we String()
  // the numeric id from the tier list before the lookup.
  const sPlusAugmentIds = perChampionAugmentTierList?.tierlist?.["S+"] ?? [];
  const topAugmentNames = sPlusAugmentIds
    .slice(0, 3)
    .map((id) => augmentManifest?.[String(id)]?.nameTRA)
    .filter((n): n is string => Boolean(n));

  const blurb = buildAugmentBlurb(name, topAugmentNames, displayPatch);

  return (
    <div className={classNames(className)}>
      <Helmet>
        <meta http-equiv="content-language" content={lang} />
      </Helmet>
      {blurb}
    </div>
  );
}
